import apiClient from './client';
import type { RecurringPurchase } from '../types';

// Backend response types (matching backend schemas)
interface SummaryResponse {
  total_spent: number;
  current_month_spent: number;
  average_per_receipt: number;
  receipt_count: number;
  current_month_count: number;
  total_savings: number;
  month_over_month_change: number;
}

interface CategorySpendingItem {
  category_name: string;
  category_id: string | null;
  total: number;
  percentage: number;
}

interface ByCategoryResponse {
  items: CategorySpendingItem[];
}

interface StoreSpendingItem {
  store_name: string;
  store_id: string | null;
  total: number;
  receipt_count: number;
}

interface ByStoreResponse {
  items: StoreSpendingItem[];
}

interface MonthlyDataItem {
  month: string;
  total: number;
  receipt_count: number;
}

interface MonthlyTrendResponse {
  items: MonthlyDataItem[];
}

interface WeeklyDataItem {
  week: string;
  total: number;
  receipt_count: number;
}

interface WeeklyTrendResponse {
  items: WeeklyDataItem[];
}

interface StoreSavingsItem {
  store_name: string;
  savings: number;
  receipt_count: number;
}

interface StoreSavingsResponse {
  items: StoreSavingsItem[];
}

export const statsApi = {
  getSummary: async (period?: string): Promise<SummaryResponse> => {
    const params = period && period !== 'all' ? { period } : {};
    const response = await apiClient.get<SummaryResponse>('/stats/summary', { params });
    const d = response.data;
    return {
      ...d,
      total_spent: Number(d.total_spent),
      current_month_spent: Number(d.current_month_spent),
      average_per_receipt: Number(d.average_per_receipt),
      receipt_count: Number(d.receipt_count),
      current_month_count: Number(d.current_month_count),
      total_savings: Number(d.total_savings),
      month_over_month_change: Number(d.month_over_month_change),
    };
  },

  getByCategory: async (filters?: { date_from?: string; date_to?: string }): Promise<CategorySpendingItem[]> => {
    const params: Record<string, string> = {};
    if (filters?.date_from) params.date_from = filters.date_from;
    if (filters?.date_to) params.date_to = filters.date_to;
    const response = await apiClient.get<ByCategoryResponse>('/stats/by-category', { params });
    return response.data.items.map(item => ({
      ...item,
      total: Number(item.total),
      percentage: Number(item.percentage),
    }));
  },

  getByStore: async (filters?: { date_from?: string; date_to?: string }): Promise<StoreSpendingItem[]> => {
    const params: Record<string, string> = {};
    if (filters?.date_from) params.date_from = filters.date_from;
    if (filters?.date_to) params.date_to = filters.date_to;
    const response = await apiClient.get<ByStoreResponse>('/stats/by-store', { params });
    return response.data.items.map(item => ({
      ...item,
      total: Number(item.total),
    }));
  },

  getMonthlyTrend: async (months: number = 12, filters?: { date_from?: string; date_to?: string }): Promise<MonthlyDataItem[]> => {
    const params: Record<string, string | number> = { months };
    if (filters?.date_from) params.date_from = filters.date_from;
    if (filters?.date_to) params.date_to = filters.date_to;
    const response = await apiClient.get<MonthlyTrendResponse>('/stats/monthly-trend', { params });
    return response.data.items;
  },

  getWeeklyTrend: async (weeks: number = 8): Promise<WeeklyDataItem[]> => {
    const response = await apiClient.get<WeeklyTrendResponse>('/stats/weekly-trend', {
      params: { weeks },
    });
    return response.data.items;
  },

  getSavingsByStore: async (): Promise<StoreSavingsItem[]> => {
    const response = await apiClient.get<StoreSavingsResponse>('/stats/savings-by-store');
    return response.data.items.map(item => ({
      ...item,
      savings: Number(item.savings),
      receipt_count: Number(item.receipt_count),
    }));
  },

  getRecurringPurchases: async (): Promise<RecurringPurchase[]> => {
    const response = await apiClient.get<{ items: RecurringPurchase[] }>('/stats/recurring-purchases');
    return response.data.items;
  },
};

// Re-export for convenience
export type {
  SummaryResponse,
  CategorySpendingItem,
  StoreSpendingItem,
  MonthlyDataItem,
  WeeklyDataItem,
  StoreSavingsItem,
};

export type { RecurringPurchase } from '../types';
